<loader [position]="{ absolute: true }" [active]="!hasVinInfo"></loader>

<main class="rp" *ngIf="vinInfo" [attr.dir]="lang === 'ar' ? 'rtl' : 'ltr'">
  <!-- ═════════════ HERO ═════════════ -->
  <section class="rp-hero" id="overview">
    <div class="rp-wrap">
      <!-- top bar -->
      <div class="rp-top">
        <div class="rp-brand">
          <img src="/assets/images/logo.png" alt="AutoIdeas" />
        </div>

        <nav class="rp-nav no-print" aria-label="Report sections">
          <button type="button" *ngFor="let l of sectionLinks" (click)="scrollTo(l.id)">
            {{ l.label }}
          </button>
        </nav>

        <!-- mobile / tablet menu -->
        <div class="rp-menu no-print">
          <button
            type="button"
            class="rp-burger"
            [class.is-open]="menuOpen"
            [attr.aria-expanded]="menuOpen"
            aria-label="Menu"
            aria-haspopup="true"
            (click)="toggleMenu($event)"
          >
            <span></span><span></span><span></span>
          </button>
          <div class="rp-drawer" *ngIf="menuOpen" (click)="$event.stopPropagation()">
            <button
              type="button"
              *ngFor="let l of sectionLinks"
              (click)="scrollTo(l.id); menuOpen = false"
            >
              {{ l.label }}
            </button>
          </div>
        </div>

        <div class="rp-actions no-print">
          <button
            id="copyBtn"
            type="button"
            class="rp-btn rp-btn--glass copy-btn"
            (click)="copyReportUrl($event)"
            [disabled]="!publicToken"
            *ngIf="authService?.userData?.token"
          >
            <span class="copy-icon">
              <svg class="ic ic--copy" viewBox="0 0 24 24"><path [attr.d]="icons.copy" /></svg>
              <svg class="ic ic--done" viewBox="0 0 24 24"><path [attr.d]="icons.check" /></svg>
            </span>
            <span class="copy-text">Copy</span>
          </button>
        </div>
      </div>

      <!-- title row -->
      <div class="rp-hero__grid">
        <figure class="rp-photo">
          <img
            [src]="mainImage ? mainImage : '/assets/images/na.png'"
            alt="Vehicle front view"
            width="200"
            height="200"
          />
          <figcaption class="rp-photo__chips">
            <span
              class="rp-chip rp-chip--solid"
              *ngIf="grade(calculatedScore) as g"
              [ngClass]="'g-' + g.cls"
            >
              <svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.shield" /></svg>
              {{ g.key | translate }} condition
            </span>
            <span class="rp-chip" *ngIf="galleryData.length">
              <svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.camera" /></svg>
              {{ galleryData.length }} photos
            </span>
          </figcaption>
        </figure>

        <div class="rp-hero__side">
          <div class="rp-title">
            <span class="rp-title__label">Vehicle</span>
            <h1>{{ vinInfo['vehicle_item_code'] || vinInfo['vehicle_item_code']['id'] }}</h1>
          </div>

          <div class="rp-scores">
            <!-- Calculated -->
            <div class="rp-score" [ngClass]="'g-' + (grade(calculatedScore)?.cls || 'none')">
              <h2>Calculated Value</h2>
              <div class="rp-ring">
                <svg viewBox="0 0 120 120" aria-hidden="true">
                  <circle class="rp-ring__track" cx="60" cy="60" r="52" />
                  <circle
                    class="rp-ring__bar"
                    cx="60"
                    cy="60"
                    r="52"
                    [style.stroke-dashoffset]="ringOffset(calculatedScore)"
                  />
                </svg>
                <strong>{{
                  +calculatedScore > 5 ? '5.0' : (+calculatedScore | number: '0.1-1')
                }}</strong>
                <small>/ 5</small>
              </div>
              <p class="rp-score__grade">
                <ng-container *ngIf="grade(calculatedScore) as g">{{
                  g.key | translate
                }}</ng-container>
              </p>
            </div>

            <!-- Perceived -->
            <div class="rp-score" [ngClass]="'g-' + (grade(PerceivedScore)?.cls || 'none')">
              <h2>Perceived Value</h2>
              <div class="rp-ring">
                <svg viewBox="0 0 120 120" aria-hidden="true">
                  <circle class="rp-ring__track" cx="60" cy="60" r="52" />
                  <circle
                    class="rp-ring__bar"
                    cx="60"
                    cy="60"
                    r="52"
                    [style.stroke-dashoffset]="ringOffset(PerceivedScore)"
                  />
                </svg>
                <strong>{{ +PerceivedScore ? PerceivedScore : 'N/A' }}</strong>
                <small *ngIf="+PerceivedScore">/ 5</small>
              </div>
              <p class="rp-score__grade">
                <ng-container *ngIf="grade(PerceivedScore) as g">{{
                  g.key | translate
                }}</ng-container>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═════════════ VALUES ═════════════ -->
  <section class="rp-values rp-wrap">
    <label class="rp-value" [class.no-print]="!marketValueChecked">
      <span class="rp-value__icon"
        ><svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.price" /></svg
      ></span>
      <span class="rp-value__body">
        <span class="rp-value__label">{{ 'market_value' | translate }}</span>
        <span class="rp-value__amount">{{ money(ratings['market_value']) }}</span>
      </span>
      <span class="rp-switch no-print" title="Include when printing">
        <input
          type="checkbox"
          [checked]="marketValueChecked"
          (change)="handleMarketValueCheck($event)"
        />
        <i></i>
      </span>
    </label>

    <label class="rp-value" [class.no-print]="!purchaseValueChecked">
      <span class="rp-value__icon"
        ><svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.bid" /></svg
      ></span>
      <span class="rp-value__body">
        <span class="rp-value__label">{{ 'purchase_value' | translate }}</span>
        <span class="rp-value__amount">{{ money(ratings['purchase_value']) }}</span>
      </span>
      <span class="rp-switch no-print" title="Include when printing">
        <input
          type="checkbox"
          [checked]="purchaseValueChecked"
          (change)="handlePurchaseValueCheck($event)"
        />
        <i></i>
      </span>
    </label>

    <label class="rp-value rp-value--repair" [class.no-print]="!totalRepairCostChecked">
      <span class="rp-value__icon"
        ><svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.wrench" /></svg
      ></span>
      <span class="rp-value__body">
        <span class="rp-value__label">{{ 'total_repair_cost' | translate }}</span>
        <span class="rp-value__amount">{{ money(ratings['total_repair_cost']) }}</span>
      </span>
      <span class="rp-switch no-print" title="Include when printing">
        <input
          type="checkbox"
          [checked]="totalRepairCostChecked"
          (change)="handleTotalRepairCostCheck($event)"
        />
        <i></i>
      </span>
    </label>
  </section>

  <!-- ═════════════ SPECS ═════════════ -->
  <section class="rp-section rp-wrap" id="specs">
    <header class="rp-section__head">
      <span class="rp-section__icon"
        ><svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.car" /></svg
      ></span>
      <h2>{{ 'specs' | translate }}</h2>
    </header>

    <div class="rp-specs">
      <div class="rp-spec" *ngFor="let s of keySpecs">
        <span class="rp-spec__icon"
          ><svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons[s.icon]" /></svg
        ></span>
        <span class="rp-spec__label">{{ s.label }}</span>
        <span class="rp-spec__value">{{ val(s.key) }}</span>
      </div>
    </div>

    <button
      type="button"
      class="rp-more no-print"
      (click)="showMoreSpecs = !showMoreSpecs"
      [class.is-open]="showMoreSpecs"
    >
      {{ showMoreSpecs ? 'Hide full details' : 'Show full details' }}
      <svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.chevron" /></svg>
    </button>

    <div class="rp-details" [class.is-open]="showMoreSpecs">
      <div class="rp-details__inner">
        <dl class="rp-dl">
          <div *ngFor="let s of moreSpecs">
            <dt>{{ s.label }}</dt>
            <dd>{{ val(s.key) }}</dd>
          </div>
        </dl>
      </div>
    </div>
  </section>

  <!-- ═════════════ OPTIONS & FLAGS ═════════════ -->
  <section class="rp-section rp-wrap" id="options">
    <div class="rp-two">
      <div>
        <header class="rp-section__head">
          <span class="rp-section__icon"
            ><svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.options" /></svg
          ></span>
          <h2 class="text-capitalize">{{ 'nav_options' | translate }}</h2>
        </header>

        <div class="rp-options">
          <article
            class="rp-option"
            *ngFor="let option of options; index as oi"
            [class.is-open]="openOption === oi"
          >
            <h3>
              <ng-container
                *ngTemplateOutlet="optTitle; context: { $implicit: option }"
              ></ng-container>
            </h3>

            <ng-container *ngIf="dataTypeChecker(option['data']) === 'object'">
              <!-- on screen: first items only -->
              <ul
                class="rp-option__list"
                [class.has-more]="option['data']?.length > maxOptionItems"
              >
                <li *ngFor="let item of option['data'] | slice: 0 : maxOptionItems">
                  <ng-container
                    *ngTemplateOutlet="optItem; context: { $implicit: item }"
                  ></ng-container>
                </li>
              </ul>

              <ng-container *ngIf="option['data']?.length > maxOptionItems">
                <!-- print: everything -->
                <ul class="rp-option__full">
                  <li *ngFor="let item of option['data']">
                    <ng-container
                      *ngTemplateOutlet="optItem; context: { $implicit: item }"
                    ></ng-container>
                  </li>
                </ul>

                <div class="rp-option__foot no-print">
                  <button
                    type="button"
                    class="rp-morechip"
                    [class.is-open]="openOption === oi"
                    [attr.aria-expanded]="openOption === oi"
                    aria-haspopup="dialog"
                    (click)="toggleOption(oi, $event, option['data'].length)"
                  >
                    +{{ option['data'].length - maxOptionItems }} more
                    <svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.chevron" /></svg>
                  </button>

                  <div
                    class="rp-pop"
                    *ngIf="openOption === oi"
                    [class.rp-pop--up]="popUp"
                    [class.rp-pop--end]="popEnd"
                    role="dialog"
                    (click)="$event.stopPropagation()"
                  >
                    <header class="rp-pop__head">
                      <strong
                        ><ng-container
                          *ngTemplateOutlet="optTitle; context: { $implicit: option }"
                        ></ng-container
                      ></strong>
                      <span class="rp-pop__count">{{ option['data'].length }}</span>
                    </header>
                    <ul class="rp-pop__list">
                      <li *ngFor="let item of option['data']">
                        <ng-container
                          *ngTemplateOutlet="optItem; context: { $implicit: item }"
                        ></ng-container>
                      </li>
                    </ul>
                  </div>
                </div>
              </ng-container>
            </ng-container>
          </article>
        </div>
      </div>

      <div>
        <header class="rp-section__head">
          <span class="rp-section__icon rp-section__icon--warn"
            ><svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.flag" /></svg
          ></span>
          <h2 class="text-capitalize">{{ 'nav_flags' | translate }}</h2>
        </header>

        <div class="rp-flags">
          <span class="rp-flag" *ngFor="let flag of flags">{{ flag }}</span>
          <span class="rp-flag rp-flag--ok" *ngIf="!flags.length">
            <svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.check" /></svg>
            No flags raised
          </span>
        </div>
      </div>
    </div>
  </section>

  <!-- ═════════════ CONDITION ═════════════ -->
  <section class="rp-section rp-wrap" id="condition">
    <header class="rp-section__head">
      <span class="rp-section__icon"
        ><svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.parts" /></svg
      ></span>
      <h2 class="text-capitalize">{{ 'nav_rating' | translate }}</h2>
    </header>

    <div class="rp-tabs no-print" role="tablist">
      <button
        type="button"
        role="tab"
        *ngFor="let c of categories"
        [class.is-active]="activeTab === c.key"
        [attr.aria-selected]="activeTab === c.key"
        (click)="activeTab = $any(c.key)"
      >
        {{ c.label }}
        <span
          class="rp-tabs__count"
          [class.is-ok]="!defects(c.items).length"
          *ngIf="c.items.length"
        >
          {{ defects(c.items).length || '✓' }}
        </span>
      </button>
    </div>

    <!-- Exterior -->
    <div class="rp-pane" [class.is-active]="activeTab === 'exterior'">
      <h3 class="rp-pane__title">Exterior</h3>
      <div class="exterior untouchable rp-diagram">
        <ng-container *ngIf="main.length">
          <app-sedan
            [data]="main"
            *ngIf="vinInfo['body_type_id'].id == vehicleBodyTypes['sedan']"
          ></app-sedan>
          <app-microbus
            [data]="main"
            *ngIf="vinInfo['body_type_id'].id == vehicleBodyTypes['bus']"
          ></app-microbus>
          <app-truck-single-cabin
            [data]="main"
            *ngIf="vinInfo['body_type_id'].id == vehicleBodyTypes['truckSingle']"
          ></app-truck-single-cabin>
          <app-truck-double-cabin
            [data]="main"
            *ngIf="vinInfo['body_type_id'].id == vehicleBodyTypes['truckDouble']"
          ></app-truck-double-cabin>
          <app-suv
            [data]="main"
            *ngIf="vinInfo['body_type_id'].id == vehicleBodyTypes['suv']"
          ></app-suv>
          <app-cargo-van
            [data]="main"
            *ngIf="vinInfo['body_type_id'].id == vehicleBodyTypes['cargo']"
          ></app-cargo-van>
        </ng-container>
        <loader [position]="{ absolute: true }" [active]="loaded"></loader>
      </div>
      <ng-container
        *ngTemplateOutlet="partCards; context: { $implicit: ratings['exterior'] }"
      ></ng-container>
    </div>

    <!-- Interior -->
    <div class="rp-pane" [class.is-active]="activeTab === 'interior'">
      <h3 class="rp-pane__title">Interior</h3>
      <div class="interior untouchable rp-diagram">
        <app-interior [vinInfoProps]="vinInfo"></app-interior>
      </div>
      <ng-container
        *ngTemplateOutlet="partCards; context: { $implicit: ratings['interior'] }"
      ></ng-container>
    </div>

    <!-- Mechanics -->
    <div class="rp-pane" [class.is-active]="activeTab === 'mechanics'">
      <h3 class="rp-pane__title">Mechanics</h3>
      <div class="mechanics untouchable rp-diagram">
        <app-mechanics [vinInfoProps]="vinInfo"></app-mechanics>
      </div>
      <ng-container
        *ngTemplateOutlet="partCards; context: { $implicit: ratings['mechanics'] }"
      ></ng-container>
    </div>

    <!-- Frame -->
    <div class="rp-pane" [class.is-active]="activeTab === 'frame'">
      <h3 class="rp-pane__title">Frame</h3>
      <div class="frame untouchable rp-diagram">
        <app-frame [vinInfoProps]="vinInfo"></app-frame>
      </div>
      <ng-container
        *ngTemplateOutlet="partCards; context: { $implicit: ratings['frame'] }"
      ></ng-container>
    </div>

    <!-- Major options -->
    <div class="rp-pane" [class.is-active]="activeTab === 'major_options'">
      <h3 class="rp-pane__title">Major Options</h3>
      <div class="major-options untouchable rp-diagram">
        <app-major-options [vinInfoProps]="vinInfo"></app-major-options>
      </div>
      <ng-container
        *ngTemplateOutlet="partCards; context: { $implicit: ratings['major_options'] }"
      ></ng-container>
    </div>
  </section>

  <!-- ═════════════ GALLERY ═════════════ -->
  <section class="rp-section rp-wrap" id="gallery">
    <header class="rp-section__head">
      <span class="rp-section__icon"
        ><svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.camera" /></svg
      ></span>
      <h2 class="text-capitalize">{{ 'nav_gallery' | translate }}</h2>
    </header>

    <div class="rp-gallery">
      <button
        type="button"
        class="rp-shot"
        *ngFor="let item of galleryData; index as gi"
        (click)="openGallery(gi)"
      >
        <img
          [src]="item['image']"
          [alt]="item['gallery_name']"
          width="100"
          height="100"
          loading="lazy"
        />
        <span [ngSwitch]="lang">
          <ng-container *ngSwitchCase="'en'">{{ item['gallery_name'] }}</ng-container>
          <ng-container *ngSwitchCase="'ar'">{{ item['gallery_name_arabic'] }}</ng-container>
        </span>
      </button>
    </div>
  </section>

  <!-- ═════════════ DISCLAIMER ═════════════ -->
  <section class="rp-section rp-wrap">
    <div class="rp-disclaimer">
      <h2>
        <svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.info" /></svg> Disclaimer
      </h2>
      <p>
        <b>Market Value</b> – Our best estimate of the price. It can vary by country, city,
        condition and how the car was used.
      </p>
      <p>
        <b>Repair Cost</b> – Approximate cost to fix the issues. Real quotes differ from one
        workshop to another.
      </p>
      <p>
        <b>Dealer Purchase Price</b> – What a dealer may pay for this car. It changes from dealer to
        dealer and over time.
      </p>
      <p>
        <b>CV (Calculated Value)</b> – A score based on how many parts are good versus faulty,
        giving more weight to serious faults.
      </p>
      <p>
        <b>PV (Perceived Value)</b> – What buyers and sellers feel the car is worth in special
        cases, e.g. a good car that has been repainted. If PV is not available, or CV is equal to or
        lower than PV, CV is used as the market value.
      </p>
    </div>
  </section>

  <div class="rp-print no-print">
    <button type="button" class="rp-btn rp-btn--dark rp-printbtn" (click)="print()">
      <span class="rp-printbtn__icon"
        ><svg class="ic" viewBox="0 0 24 24"><path [attr.d]="icons.print" /></svg
      ></span>
      <span class="rp-printbtn__label">Print report</span>
    </button>
  </div>

  <!-- ═════════════ LIGHTBOX ═════════════ -->
  <div
    class="rp-viewer no-print"
    dir="ltr"
    *ngIf="viewer as vw"
    role="dialog"
    aria-modal="true"
    aria-label="Image viewer"
  >
    <header class="rp-viewer__bar">
      <div class="rp-viewer__info">
        <span class="rp-viewer__count">{{ vw.index + 1 }} / {{ vw.items.length }}</span>
        <span class="rp-viewer__cap" *ngIf="vw.items[vw.index].caption">{{
          vw.items[vw.index].caption
        }}</span>
      </div>
      <div class="rp-viewer__tools">
        <div class="rp-viewer__zoom">
          <button type="button" aria-label="Zoom out" title="Zoom out" (click)="zoomOut()">
            <svg viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.35-4.35M8 11h6" />
            </svg>
          </button>
          <button
            type="button"
            class="rp-viewer__pct"
            aria-label="Reset zoom"
            title="Reset zoom"
            (click)="resetZoom()"
          >
            <span id="rpVwPct">100%</span>
          </button>
          <button type="button" aria-label="Zoom in" title="Zoom in" (click)="zoomIn()">
            <svg viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
            </svg>
          </button>
        </div>
        <button type="button" class="rp-viewer__close" aria-label="Close" (click)="closeViewer()">
          <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </div>
    </header>

    <div class="rp-viewer__main">
      <div class="rp-viewer__stage" id="rpVwStage">
        <img
          id="rpVwImg"
          [src]="vw.items[vw.index].src"
          [alt]="vw.items[vw.index].caption"
          draggable="false"
        />
      </div>
      <ng-container *ngIf="vw.items.length > 1">
        <button
          type="button"
          class="rp-viewer__nav is-prev"
          aria-label="Previous"
          (click)="step(-1)"
        >
          <svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <button type="button" class="rp-viewer__nav is-next" aria-label="Next" (click)="step(1)">
          <svg viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
        </button>
      </ng-container>
    </div>

    <div class="rp-viewer__thumbs" *ngIf="vw.items.length > 1">
      <button
        type="button"
        class="rp-viewer__thumb"
        *ngFor="let t of vw.items; index as ti"
        [class.is-active]="ti === vw.index"
        [attr.aria-label]="'Image ' + (ti + 1)"
        (click)="goTo(ti, ti > vw.index ? 1 : -1)"
      >
        <img [src]="t.src" alt="" loading="lazy" draggable="false" />
      </button>
    </div>
  </div>

  <!-- ═════════════ AI ASSISTANT PANEL (separate component) ═════════════ -->
  <app-report-assistant
    class="no-print"
    [(open)]="assistantOpen"
    [vinInfo]="vinInfo"
    [ratings]="ratings"
    [flags]="flags"
    [options]="options"
    [calculatedScore]="calculatedScore"
    [perceivedScore]="PerceivedScore"
    [specFields]="allSpecFields"
    [direction]="lang === 'ar' ? 'rtl' : 'ltr'"
  ></app-report-assistant>
</main>

<!-- ═════════════ SHARED: option labels ═════════════ -->
<ng-template #optTitle let-option>
  <ng-container *ngIf="lang == 'en'">{{ option['title'] }}</ng-container>
  <ng-container *ngIf="lang == 'ar'">{{ option['arabic_title'] }}</ng-container>
</ng-template>
<ng-template #optItem let-item>
  <ng-container *ngIf="lang == 'en'">{{ item['title'] }}</ng-container>
  <ng-container *ngIf="lang == 'ar'">{{ item['arabic_title'] }}</ng-container>
</ng-template>

<!-- ═════════════ SHARED: part cards ═════════════ -->
<ng-template #partCards let-items>
  <div class="rp-parts">
    <figure
      class="rp-part"
      *ngFor="let item of items"
      [ngClass]="'sev-' + (item.vpcs_severity || 0)"
    >
      <div class="rp-part__head">
        <h4>{{ item.parent_part }}</h4>
        <span class="rp-sev">{{ severityLabel(item.vpcs_severity) }}</span>
      </div>

      <div class="rp-part__imgs" *ngIf="item.vpcs_old_image || item.vpcs_new_image">
        <div *ngIf="item.vpcs_old_image && item.vpcs_old_image.length">
          <h6>{{ 'condition_modal.before' | translate }}</h6>
          <div class="rp-part__row">
            <ng-container *ngFor="let img of item.vpcs_old_image; index as i">
              <img
                *ngIf="img[i + 1]"
                [src]="basePath.slice(0,-1) + item.image_url + '/' + img[i + 1]"
                width="155"
                height="100"
                loading="lazy"
                class="rp-zoomable"
                role="button"
                tabindex="0"
                (click)="openPartImage(item, img[i + 1])"
                (keydown.enter)="openPartImage(item, img[i + 1])"
              />
            </ng-container>
          </div>
        </div>
        <div *ngIf="item.vpcs_new_image && item.vpcs_new_image.length">
          <h6>{{ 'condition_modal.after' | translate }}</h6>
          <div class="rp-part__row">
            <ng-container *ngFor="let img of item.vpcs_new_image; index as i">
              <img
                *ngIf="img[i + 1]"
                [src]="item.image_url + '/' + img[i + 1]"
                width="155"
                height="100"
                loading="lazy"
                class="rp-zoomable"
                role="button"
                tabindex="0"
                (click)="openPartImage(item, img[i + 1])"
                (keydown.enter)="openPartImage(item, img[i + 1])"
              />
            </ng-container>
          </div>
        </div>
      </div>

      <figcaption>
        <dl>
          <div>
            <dt>{{ 'condition_modal.condition' | translate }}</dt>
            <dd>
              <ng-container *ngFor="let condition of item.vpcs_conditions; index as i">
                {{ condition }}<span *ngIf="i < item.vpcs_conditions.length - 1">, </span>
              </ng-container>
              <ng-container *ngIf="!item.vpcs_conditions?.length">N/A</ng-container>
            </dd>
          </div>
          <div>
            <dt>{{ 'condition_modal.labour_cost' | translate }}</dt>
            <dd>{{ +item.labour_cost ? 'SAR ' + item.labour_cost : 'N/A' }}</dd>
          </div>
          <div>
            <dt>{{ 'condition_modal.parts_cost' | translate }}</dt>
            <dd>{{ +item.part_cost ? 'SAR ' + item.part_cost : 'N/A' }}</dd>
          </div>
          <div class="is-total">
            <dt>{{ 'condition_modal.cost_of_repair' | translate }}</dt>
            <dd>{{ +item.repair_cost ? 'SAR ' + item.repair_cost : 'N/A' }}</dd>
          </div>
        </dl>
        <p class="rp-part__desc">
          <b>{{ 'condition_modal.description' | translate }}:</b> {{ item.description || 'N/A' }}
        </p>
      </figcaption>
    </figure>
  </div>
</ng-template>

<div class="d-none" *ngIf="!hasVinInfo">
  <iframe src="/vam/information" frameborder="0"></iframe>
</div>